import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Auth } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import uniq from 'lodash/uniq';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

import { logoutAction, setCurrentUserAction } from 'store/actions/userActions';
import { getCurrentUserRequest } from 'api/requests/userRequests';
import { getCookie, removeCookie } from 'utils/cookies';
import history from 'utils/history';
import sendNotification, { ENotificationTypes } from 'utils/sendNotification';
import { getTokens, setTokens } from 'utils/tokensActions';
import { COOKIE_KEYS, isDevelopment } from 'utils/constants/constants';

const convertCognitoData = ({ signInUserSession }: any) => {
  const idToken = signInUserSession.idToken.jwtToken;
  const accessToken = signInUserSession.accessToken.jwtToken;
  const refreshToken = signInUserSession.refreshToken.token;
  const { payload } = signInUserSession.idToken;

  return ({
    idToken,
    accessToken,
    refreshToken,
    tokenPayload: payload,
    permissions: uniq(payload['cognito:groups'].join(',').replace('User', 'Supplier').split(',')),
    isEmailVerified: payload.email_verified,
    isNative: payload['custom:source'] === '1',
    needRegister: !payload['custom:autods_user_id'],
  });
};

const getCognitoSession = async() => convertCognitoData(await Auth.currentAuthenticatedUser());

export const refreshSession = async({ idToken, accessToken, refreshToken }: any) => {
  const IdToken = new AmazonCognitoIdentity.CognitoIdToken({ IdToken: idToken });
  const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({ AccessToken: accessToken });
  const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({ RefreshToken: refreshToken });
  const sessionData = { IdToken, AccessToken, RefreshToken };
  const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData);
  const userPool = new AmazonCognitoIdentity.CognitoUserPool({
    UserPoolId: process.env.REACT_APP_USER_POOL_ID || '',
    ClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID || '',
  });
  const cognitoUser = new AmazonCognitoIdentity.CognitoUser({ Username: IdToken.payload.email, Pool: userPool });

  cognitoUser.setSignInUserSession(session);

  window.console.log(`%c${IdToken.payload.email} session was restored`, 'color: #FFCB6B');

  return await getCognitoSession();
};

export const getCognitoUser = async() => {
  try {
    const cognitoUser = await getCognitoSession();
    const { idToken, accessToken, refreshToken } = getTokens();

    if (idToken && accessToken && refreshToken) {
      const { payload: { email: emailFromToken } } = new AmazonCognitoIdentity.CognitoIdToken({ IdToken: idToken });

      if (emailFromToken !== cognitoUser?.tokenPayload.email) {
        return await refreshSession({ idToken, accessToken, refreshToken });
      }

      return cognitoUser;
    }
  } catch (err) {
    const { idToken, accessToken, refreshToken } = getTokens();

    if (idToken && accessToken && refreshToken) {
      return await refreshSession({ idToken, accessToken, refreshToken });
    }

    isDevelopment && window.console.log(`%c${err}`, 'color: #eb4242');
  }
};

export default function() {
  const dispatch = useDispatch();

  const setUser = async(cognitoUser: any) => {
    if (!cognitoUser.isNative) {
      const sessionSearch = getCookie(COOKIE_KEYS.sessionSearch);

      history.push({ search: sessionSearch || '' });
      removeCookie(COOKIE_KEYS.sessionSearch);
    }

    const { data, error } = await getCurrentUserRequest(cognitoUser);

    if (!error) {
      dispatch(setCurrentUserAction({ data }));
    } else {
      dispatch(logoutAction(true));
    }
  };

  const authListener = ({ payload: { event, data } }: any) => {
    switch (event) {
      case 'signIn': {
        const { idToken, accessToken, refreshToken, ...cognitoUser } = convertCognitoData(data);

        if (!cognitoUser.permissions) {
          sendNotification(
            ENotificationTypes.Error,
            'Your user don’t have any permission assigned yet, If the '
            + 'issue persist please ask your account owner to disable your permissions and than enable them again',
          );

          return;
        }

        setTokens({ idToken, accessToken, refreshToken });

        if (cognitoUser.isNative) {
          setUser({ ...cognitoUser, idToken });
        }

        break;
      }

      case 'signOut': {
        dispatch(logoutAction());
        break;
      }

      default: {
        break;
      }
    }
  };

  const checkCurrentUser = async() => {
    const cognitoUser = await getCognitoUser();

    if (cognitoUser) {
      setUser(cognitoUser);
    } else {
      dispatch(logoutAction(true));
    }
  };

  useEffect(() => {
    Hub.listen('auth', authListener);
    checkCurrentUser();

    return () => Hub.remove('auth', authListener);
  }, []);
};

